import { logger } from "@/services/Logger";

class WakeLockService {
  private wakeLock: WakeLockSentinel | null = null;
  private readonly isSupported: boolean = false;
  private reacquireTimer: NodeJS.Timeout | null = null;
  private isEnabled: boolean = false;

  constructor() {
    this.isSupported = "wakeLock" in navigator;

    if (this.isSupported) {
      // Re-acquire wake lock when page becomes visible
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      // Re-acquire wake lock when coming back online
      window.addEventListener("online", this.handleOnline);

      // Try to re-acquire wake lock periodically
      this.startPeriodicReacquire();
    }
  }

  private readonly handleVisibilityChange = async () => {
    if (document.visibilityState === "visible" && this.isEnabled) {
      logger.info(
        "WakeLockService",
        "[WakeLock] Page visible, re-acquiring wake lock"
      );
      await this.request();
    }
  };

  private readonly handleOnline = async () => {
    if (this.isEnabled) {
      logger.info(
        "WakeLockService",
        "[WakeLock] Network online, re-acquiring wake lock"
      );
      await this.request();
    }
  };

  private startPeriodicReacquire() {
    // Try to re-acquire wake lock every 30 seconds if not held
    this.reacquireTimer = setInterval(async () => {
      if (this.isEnabled && !this.wakeLock) {
        logger.info(
          "WakeLockService",
          "[WakeLock] Periodic re-acquire attempt"
        );
        await this.request();
      }
    }, 30000);
  }

  async request(): Promise<boolean> {
    if (!this.isSupported) {
      logger.error("WakeLockService", "[WakeLock] Wake Lock API not supported");
      return false;
    }

    try {
      // Release existing lock if any
      if (this.wakeLock) {
        await this.wakeLock.release();
        this.wakeLock = null;
      }

      // Request new wake lock
      this.wakeLock = await navigator.wakeLock.request("screen");
      this.isEnabled = true;

      logger.info("WakeLockService", "[WakeLock] Wake lock acquired");

      // Listen for release events
      this.wakeLock.addEventListener("release", () => {
        logger.info("WakeLockService", "[WakeLock] Wake lock released");
        this.wakeLock = null;

        // Try to re-acquire immediately if still enabled
        if (this.isEnabled && document.visibilityState === "visible") {
          setTimeout(() => this.request(), 1000);
        }
      });

      return true;
    } catch (error) {
      logger.error(
        "WakeLockService",
        "[WakeLock] Failed to acquire wake lock:",
        error
      );
      return false;
    }
  }

  async release() {
    this.isEnabled = false;

    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
        logger.info(
          "WakeLockService",
          "[WakeLock] Wake lock manually released"
        );
      } catch (error) {
        logger.error(
          "WakeLockService",
          "[WakeLock] Error releasing wake lock:",
          error
        );
      }
    }
  }

  cleanup() {
    this.release();

    if (this.reacquireTimer) {
      clearInterval(this.reacquireTimer);
      this.reacquireTimer = null;
    }

    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("online", this.handleOnline);
  }

  isActive(): boolean {
    return this.wakeLock !== null;
  }

  getStatus(): { supported: boolean; active: boolean; enabled: boolean } {
    return {
      supported: this.isSupported,
      active: this.isActive(),
      enabled: this.isEnabled,
    };
  }
}

export const wakeLockService = new WakeLockService();
